import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import Cookies from "js-cookie";
import { useOrganization } from "../context/OrganizationContext";
import CybeSelect from "./CybeSelect";
import "./AlertWorkspace.css";
import {text} from "./alertQueue";

const asList = value => Array.isArray(value) ? value.map(v=>text(v,'')).filter(Boolean) : typeof value==='string' ? [value] : [];
function assessmentPayload(value) {
 if(!value || typeof value!=='object' || Array.isArray(value))return null;
 return {...value,summary:text(value.summary,'No assessment summary available.'),recommended_action:text(value.recommended_action,'Investigate'),reasoning:asList(value.reasoning)};
}
async function detailFetch(url, options={}) {
 const controller=new AbortController();
 const external=options.signal;
 const abort=()=>controller.abort();
 if(external?.aborted)controller.abort();
 external?.addEventListener('abort',abort,{once:true});
 const timer=setTimeout(abort,15000);
 try{const response=await fetch(url,{...options,signal:controller.signal,cache:'no-store'});let data;try{data=await response.json();}catch(error){if(error.name==='AbortError')throw error;throw new Error('The server returned an unreadable response. Refresh the current status before retrying.');}return {ok:response.ok,status:response.status,json:async()=>data};}
 catch(error){if(error.name==='AbortError' && !external?.aborted)throw new Error('Request timed out. Refresh the current status before retrying.');throw error;}
 finally{clearTimeout(timer);external?.removeEventListener('abort',abort);}
}

function formatDate(value) {
  if (!value) return "-";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
}

function scoreClass(score) {
  const value = Number(score || 0);
  if (value >= 70) return "bg-red-50 text-red-700 ring-red-200";
  if (value >= 40) return "bg-amber-50 text-amber-700 ring-amber-200";
  return "bg-emerald-50 text-emerald-700 ring-emerald-200";
}

function severityClass(severity) {
  const value = String(severity || "").toLowerCase();
  if (value === "critical" || value === "high") return "bg-red-50 text-red-700 ring-red-200";
  if (value === "medium") return "bg-amber-50 text-amber-700 ring-amber-200";
  return "bg-sky-50 text-sky-700 ring-sky-200";
}

function statusClass(status) {
  const value = String(status || "").toLowerCase();
  if (value === "resolved") return "bg-emerald-50 text-emerald-700 ring-emerald-200";
  if (value === "investigating") return "bg-amber-50 text-amber-700 ring-amber-200";
  return "bg-sky-50 text-sky-700 ring-sky-200";
}

function Badge({ children, className = "" }) {
  return <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${className}`}>{children}</span>;
}

export default function ThreatDetailsModal({ isOpen, onClose, threat, onUpdated }) {
  const { activeOrganization } = useOrganization();
  const dialogRef=useRef(null);
  const reads=useRef(null);
  const readVersion=useRef(0);
  const draftDirty=useRef(false);
  const [assessmentError,setAssessmentError]=useState("");
  useEffect(()=>{
    const dialog=dialogRef.current;
    if(isOpen && dialog && !dialog.open) dialog.showModal();
    const previous=document.body.style.overflow;
    if(isOpen)document.body.style.overflow="hidden";
    return ()=>{document.body.style.overflow=previous;};
  },[isOpen]);
  const [detail, setDetail] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [statusDraft, setStatusDraft] = useState("open");
  const [assigneeDraft, setAssigneeDraft] = useState("");
  const [resolutionDraft, setResolutionDraft] = useState("");
  const [responseHistory,setResponseHistory]=useState(null);
  const [responseHistoryError,setResponseHistoryError]=useState("");
  const [responseBusy, setResponseBusy] = useState(false);
  const [proposalError,setProposalError]=useState("");
  const [proposalNotice,setProposalNotice]=useState("");
  const [intelProfile, setIntelProfile] = useState(null);
  const [analystFeedback, setAnalystFeedback] = useState(null);
  const [feedbackBusy, setFeedbackBusy] = useState(false);

  const token = Cookies.get("token") || localStorage.getItem("token");
  const baseUrl = process.env.REACT_APP_CYBEDEFENDER_AI_URL || "/api";
  const isTenantAlert = Boolean(threat?.isSyslog && activeOrganization?.guid);
  const canManage = isTenantAlert && String(detail?.organization?.membership_role || activeOrganization?.membership_role || "").toLowerCase() === "admin";

  const tenantBase = useMemo(() => {
    if (!isTenantAlert || !threat?.id) return null;
    return `${baseUrl}/cybedefender/organizations/${encodeURIComponent(activeOrganization.guid)}/security-alerts/${encodeURIComponent(threat.id)}`;
  }, [activeOrganization?.guid, baseUrl, isTenantAlert, threat?.id]);

  const syncDrafts = useCallback((data) => {
    setStatusDraft(data?.alert?.status || "open");
    setAssigneeDraft(data?.investigation?.assigned_to?.guid || "");
    setResolutionDraft(data?.investigation?.resolution_summary || "");
  }, []);

  const loadTenantInvestigation = useCallback(async () => {
    if (!tenantBase) return;
    reads.current?.abort();const controller=new AbortController();reads.current=controller;
    const version=++readVersion.current;
    setLoading(true);setError("");setAssessmentError("");setResponseHistoryError("");
    const headers={Authorization:`Bearer ${token}`};
    const options={headers,signal:controller.signal};
    try {
      // Optional intelligence failure must not hide the alert or its investigation.
      const results=await Promise.allSettled([
        detailFetch(tenantBase,options).then(async r=>{if(!r.ok)throw new Error(r.status===404?"This alert is no longer available.":r.status===403?"You no longer have access to this workspace.":"Investigation request failed ("+r.status+").");return r.json();}),
        detailFetch(tenantBase+"/ai-assessment",options).then(async r=>{if(!r.ok)throw new Error("AI assessment unavailable ("+r.status+").");return r.json();}),
        detailFetch(tenantBase+"/threat-intelligence",options).then(async r=>{if(!r.ok)throw new Error("Threat intelligence unavailable ("+r.status+").");return r.json();}),
        detailFetch(tenantBase+"/response-actions",options).then(async r=>{if(!r.ok)throw new Error("Response history unavailable ("+r.status+").");const data=await r.json();if(!Array.isArray(data.actions)||!Array.isArray(data.blocks))throw new Error("Response history unavailable.");return data;})
      ]);
      if(controller.signal.aborted || version!==readVersion.current)return;
      if(results[0].status!=="fulfilled")throw results[0].reason;
      if(!results[0].value?.alert)throw new Error("The server did not return this alert.");
      setDetail(results[0].value);if(!draftDirty.current)syncDrafts(results[0].value);
      setAssessment(results[1].status==="fulfilled"?assessmentPayload(results[1].value.assessment):null);
      setAssessmentError(results[1].status==="rejected"?results[1].reason.message:"");
      const intel=results[2].status==="fulfilled"?results[2].value:null;
      setIntelProfile(intel?.profile || null);setAnalystFeedback(intel?.feedback || null);
      setResponseHistory(results[3].status==="fulfilled"?results[3].value:null);
      setResponseHistoryError(results[3].status==="rejected"?results[3].reason.message:"");
    } catch(loadError) {
      if(!controller.signal.aborted && version===readVersion.current)setError(loadError.message || "Unable to load this investigation.");
    } finally {
      if(!controller.signal.aborted && version===readVersion.current)setLoading(false);
    }
  },[tenantBase,token,syncDrafts]);

  const loadLegacyAssessment = useCallback(async () => {
    if (!threat?.id) return;
    reads.current?.abort();const controller=new AbortController();reads.current=controller;
    const version=++readVersion.current;
    setLoading(true);setError("");setAssessmentError("");
    try {
      const userId=Cookies.get("guid") || "";
      const response=await detailFetch(`${baseUrl}/cybedefender/ai-assessment?alertId=${encodeURIComponent(threat.id)}&userId=${encodeURIComponent(userId)}`,{headers:{Authorization:`Bearer ${token}`},signal:controller.signal});
      if(!response.ok)throw new Error(response.status===404?"No assessment is available for this upload alert.":"Assessment unavailable ("+response.status+").");
      const data=await response.json();
      if(controller.signal.aborted || version!==readVersion.current)return;
      setAssessment(assessmentPayload(data.assessment));
    } catch(loadError) {
      if(!controller.signal.aborted && version===readVersion.current)setAssessmentError(loadError.message || "Unable to load the assessment.");
    } finally {
      if(!controller.signal.aborted && version===readVersion.current)setLoading(false);
    }
  },[baseUrl,threat?.id,token]);

  useEffect(() => {
    if (!isOpen || !threat?.id) return;
    draftDirty.current=false;
    setProposalError("");setProposalNotice("");
    setDetail(null);
    setAssessment(null);
    setNoteText("");
    setResponseHistory(null);setResponseHistoryError("");
    setIntelProfile(null);
    setAnalystFeedback(null);
    setError("");
    if (isTenantAlert) loadTenantInvestigation();
    else loadLegacyAssessment();
    return ()=>{reads.current?.abort();readVersion.current+=1;};
  }, [isOpen, threat?.id, isTenantAlert, loadTenantInvestigation, loadLegacyAssessment]);

  const saveInvestigation = async () => {
    if (!tenantBase || !canManage) return;
    setSaving(true);
    setError("");
    try {
      const response = await detailFetch(tenantBase, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          status: statusDraft,
          assigned_user_guid: assigneeDraft || null,
          resolution_summary: resolutionDraft
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || `Update failed: ${response.status}`);
      setDetail(data);
      draftDirty.current=false;syncDrafts(data);
      if (onUpdated) onUpdated();
    } catch (saveError) {
      setError(saveError.message || "Failed to update investigation");
    } finally {
      setSaving(false);
    }
  };

  const addNote = async () => {
    if (!tenantBase || !canManage || !noteText.trim()) return;
    setSaving(true);
    setError("");
    try {
      const response = await detailFetch(`${tenantBase}/notes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ text: noteText.trim() })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || `Note failed: ${response.status}`);
      setDetail(data);
      if(!draftDirty.current)syncDrafts(data);
      setNoteText("");
      if (onUpdated) onUpdated();
    } catch (noteError) {
      setError(noteError.message || "Failed to add note");
    } finally {
      setSaving(false);
    }
  };

  const proposeBlock = async () => {
    if (!tenantBase || !canManage || responseBusy) return;
    setResponseBusy(true);
    setProposalError("");setProposalNotice("");
    try {
      const response = await detailFetch(`${tenantBase}/response-actions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          action_type: "block_ip",
          reason: null
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || `Response proposal failed: ${response.status}`);
      if(!data.action?.action_id || !data.action?.status)throw new Error("The server did not return a saved response. Refresh status before retrying.");
      setProposalNotice(data.action.status==="proposed"?"Block proposed. Open Response Center to review it.":data.action.status==="rejected"?"Proposal rejected by policy: "+text(data.action.reason).replaceAll("_"," "):"A saved response was returned with status "+text(data.action.status)+". Open Response Center to review it.");
      setResponseHistory(previous=>({...previous,actions:[data.action,...(previous?.actions||[]).filter(a=>a.action_id!==data.action.action_id)],blocks:previous?.blocks||[]}));
    } catch (responseError) {
      setProposalError(responseError.message || "Failed to propose response action");
    } finally {
      setResponseBusy(false);
    }
  };

  const submitAnalystFeedback = async (verdict) => {
    if (!tenantBase || !canManage || feedbackBusy) return;
    setFeedbackBusy(true);
    setError("");
    try {
      const response = await detailFetch(`${tenantBase}/ai-feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ verdict })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Feedback failed: ${response.status}`);
      setAnalystFeedback(data.feedback || null);
      await loadTenantInvestigation();
      if (onUpdated) onUpdated();
    } catch (feedbackError) {
      setError(feedbackError.message || "Failed to save analyst feedback");
    } finally {
      setFeedbackBusy(false);
    }
  };

  const refreshThreatIntel = async () => {
    if (!tenantBase || !canManage || feedbackBusy) return;
    setFeedbackBusy(true);
    setError("");
    try {
      const response = await detailFetch(`${tenantBase}/threat-intelligence/refresh`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Refresh failed: ${response.status}`);
      setIntelProfile(data.profile || null);
      await loadTenantInvestigation();
    } catch (refreshError) {
      setError(refreshError.message || "Failed to refresh threat intelligence");
    } finally {
      setFeedbackBusy(false);
    }
  };

  if (!isOpen || !threat) return null;

  const currentAlert = detail?.alert || {
    alert_id: threat.id,
    status: threat.status,
    severity: threat.severity,
    threat_type: threat.threatType,
    matched_rule: threat.matchedRule,
    source_ip: threat.sourceIp,
    message: threat.message,
    threat_score: threat.threatScore,
    created_at: threat.date,
    source_device: threat.sourceDevice,
    collector_device_name: threat.collectorDevice
  };

  const responseHref="/responses?alert="+encodeURIComponent(threat.id)+"&organization="+encodeURIComponent(activeOrganization?.guid||"");
  const completedBlockResponse=(responseHistory?.actions||[]).some(a=>a.action_type==="block_ip" && a.status==="executed");
  const pendingResponse=(responseHistory?.actions||[]).some(a=>!["executed","failed","rejected","simulated","cancelled"].includes(a.status)) || (responseHistory?.blocks||[]).some(b=>["active","unblocking"].includes(b.status));

  return (
    <dialog ref={dialogRef} className="aq-drawer" aria-label={isTenantAlert?"Alert investigation":"Upload alert details"} onCancel={event=>{event.preventDefault();if(!saving && !responseBusy && !feedbackBusy)onClose();}}>
      <div className="aq-drawer-inner">
        <div className="aq-drawer-head">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.16em] text-cyan-700">
              {isTenantAlert ? "Security investigation" : "Threat details"}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-3">
              <h2 className="text-xl font-semibold text-slate-950">{currentAlert.threat_type || "Security Alert"}</h2>
              <Badge className={severityClass(currentAlert.severity)}>{String(currentAlert.severity || "unknown").toUpperCase()}</Badge>
              <Badge className={statusClass(currentAlert.status)}>{String(currentAlert.status || "open").toUpperCase()}</Badge>
            </div>
            <p className="mt-1 font-mono text-xs text-slate-400">{currentAlert.alert_id || threat.id}</p>
          </div>
          <button type="button" onClick={onClose} disabled={saving || responseBusy || feedbackBusy} autoFocus className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900" aria-label="Close investigation">✕</button>
        </div>

        <div className="aq-drawer-body">
          {!isTenantAlert && <div className="aq-readonly">Personal upload alert · read-only evidence. Workspace investigation and gateway response actions require a workspace detection.</div>}
          {assessmentError && <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" role="status">{assessmentError}<button type="button" className="ml-3 underline" onClick={()=>isTenantAlert?loadTenantInvestigation():loadLegacyAssessment()}>Retry assessment</button></div>}
          {error && <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">{error}<button type="button" className="ml-3 underline" disabled={saving || responseBusy || feedbackBusy} onClick={()=>isTenantAlert?loadTenantInvestigation():loadLegacyAssessment()}>Retry details</button></div>}
          {loading ? (
            <div className="flex min-h-[360px] items-center justify-center text-sm text-slate-500">Loading investigation...</div>
          ) : (
            <div className="space-y-6">
              {isTenantAlert && detail && (
                  <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h3 className="font-semibold text-slate-900">Investigation workflow</h3>
                        <p className="text-xs text-slate-500">Assignment, lifecycle, and resolution</p>
                      </div>
                      {!canManage && <Badge className="bg-slate-100 text-slate-600 ring-slate-200">READ ONLY</Badge>}
                    </div>
                    <div className="mt-4 grid gap-4 md:grid-cols-3">
                      <label className="text-sm text-slate-600">Status
                        <CybeSelect value={statusDraft} onChange={(event) => {draftDirty.current=true;setStatusDraft(event.target.value);}} disabled={!canManage || saving} className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-900 disabled:bg-slate-50">
                          <option value="open">Open</option><option value="investigating">Investigating</option><option value="resolved">Resolved</option>
                        </CybeSelect>
                      </label>
                      <label className="text-sm text-slate-600">Assigned to
                        <CybeSelect value={assigneeDraft} onChange={(event) => {draftDirty.current=true;setAssigneeDraft(event.target.value);}} disabled={!canManage || saving} className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-900 disabled:bg-slate-50">
                          <option value="">Unassigned</option>
                          {(detail.assignable_members || []).map((member) => <option key={member.guid} value={member.guid}>{member.name} — {member.role}</option>)}
                        </CybeSelect>
                      </label>
                      <div className="text-sm text-slate-600">Acknowledged
                        <div className="mt-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700">{formatDate(detail.investigation?.acknowledged_at)}</div>
                      </div>
                    </div>
                    <label className="mt-4 block text-sm text-slate-600">Resolution summary (optional)
                      <textarea value={resolutionDraft} onChange={(event) => {draftDirty.current=true;setResolutionDraft(event.target.value);}} disabled={!canManage || saving} rows="3" placeholder="Document why and how this alert was resolved..." className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-slate-900 disabled:bg-slate-50" />
                    </label>
                    {canManage && <div className="mt-4 flex justify-end"><button type="button" onClick={saveInvestigation} disabled={saving} className="rounded-lg bg-cyan-700 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-800 disabled:opacity-50">{saving ? "Saving..." : "Save investigation"}</button></div>}
                  </section>
              )}
              {isTenantAlert && detail && <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3"><div><h3 className="font-semibold text-slate-900">Response outcomes</h3><p className="text-xs text-slate-500">Saved actions and block state for this alert</p><button type="button" onClick={loadTenantInvestigation} disabled={saving||responseBusy||feedbackBusy} className="mt-2 text-xs font-semibold text-cyan-800 underline">Refresh status</button></div><a href={responseHref} className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white">Open Response Center →</a></div>
                {responseHistoryError ? <p className="mt-3 text-sm text-amber-800" role="status">{responseHistoryError} Open Response Center to check the current state.</p> : <>
                  {!responseHistory?.actions?.length && !responseHistory?.blocks?.length && <p className="mt-3 text-sm text-slate-500">No response recorded for this alert.</p>}
                  {(responseHistory?.blocks||[]).slice(0,3).map(block=><div key={block.block_id} className="mt-3 rounded-lg border border-slate-200 p-3 text-sm"><strong>{text(block.target_ip)} · {text(block.status).replaceAll("_"," ")}</strong><div className="mt-1 text-xs text-slate-500">{text(block.executor)} · Applied {formatDate(block.blocked_at)} · Expires {formatDate(block.expires_at)}{block.unblocked_at ? " · Removed "+formatDate(block.unblocked_at) : ""}</div></div>)}
                  {(responseHistory?.actions||[]).slice(0,3).map(action=><div key={action.action_id} className="mt-3 flex flex-wrap justify-between gap-2 border-t border-slate-100 pt-3 text-sm"><div><strong>{text(action.action_type).replaceAll("_"," ")}</strong> · {text(action.target_ip)}<div className="mt-1 text-xs text-slate-500">{formatDate(action.requested_at)} · {action.routing?.gateway?.name || action.routing?.site?.name || "Route shown in Response Center"}</div></div><div><Badge className="bg-slate-100 text-slate-700 ring-slate-200">{text(action.status).replaceAll("_"," ")}</Badge>{action.required_approvals>0 && <div className="mt-1 text-xs text-slate-500">Approvals {action.approval_count||0}/{action.required_approvals}</div>}</div></div>)}
                  {(responseHistory?.actions?.length>3 || responseHistory?.blocks?.length>3) && <p className="mt-3 text-xs text-slate-500">Previewing the newest 3 actions and first 3 blocks. Open Response Center for the full list.</p>}
                  {(responseHistory?.actions_truncated||responseHistory?.blocks_truncated) && <p className="mt-3 text-xs text-slate-500">Showing up to 100 actions and 100 blocks. Active blocks appear first.</p>}
                </>}
                <p className="mt-3 text-xs text-slate-500">Executed is an action outcome. The block record shows whether enforcement is still active. Expiry is confirmed by the executor.</p>
                {proposalError && <p role="alert" className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{proposalError}</p>}
                {proposalNotice && <p role="status" className="mt-3 rounded-lg border border-cyan-200 bg-cyan-50 p-3 text-sm text-cyan-800">{proposalNotice}</p>}
                {completedBlockResponse && <p className="mt-3 text-sm text-slate-600">This alert already has an executed block response. <a href={responseHref} className="font-semibold text-cyan-800 underline">View its saved response and history</a>.</p>}
                {canManage && currentAlert.source_ip && responseHistory && !responseHistoryError && <div className="mt-4">{completedBlockResponse || pendingResponse || responseHistory.actions_truncated || responseHistory.blocks_truncated ? <p className="text-xs text-slate-600">Review the existing response in Response Center before proposing another block.</p> : <button type="button" onClick={proposeBlock} disabled={responseBusy} className="rounded-lg bg-cyan-700 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">{responseBusy?"Proposing...":"Propose block"}</button>}</div>}
              </section>}
              <div className="grid gap-5 xl:grid-cols-[1.4fr_1fr]">
                <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="mb-4 flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-slate-900">Alert evidence</h3>
                      <p className="text-xs text-slate-500">Tenant-owned detection context</p>
                    </div>
                    <span className={`rounded-lg px-3 py-1 text-sm font-semibold ring-1 ring-inset ${scoreClass(currentAlert.threat_score)}`}>{currentAlert.threat_score == null ? "Score unavailable" : currentAlert.threat_score+"/100"}</span>
                  </div>
                  <div className="grid gap-4 text-sm sm:grid-cols-2">
                    <div><div className="text-xs text-slate-400">Source IP</div><div className="mt-1 font-medium text-slate-900">{currentAlert.source_ip || "-"}</div></div>
                    <div><div className="text-xs text-slate-400">Source device</div><div className="mt-1 font-medium text-slate-900">{currentAlert.source_device || "-"}</div></div>
                    <div><div className="text-xs text-slate-400">Collector</div><div className="mt-1 font-medium text-slate-900">{currentAlert.collector_device_name || "-"}</div></div>
                    <div><div className="text-xs text-slate-400">Matched rule</div><div className="mt-1 font-medium text-slate-900">{currentAlert.matched_rule || "-"}</div></div>
                    <div><div className="text-xs text-slate-400">Principal</div><div className="mt-1 font-medium text-slate-900">{currentAlert.principal || "-"}</div></div>
                    <div><div className="text-xs text-slate-400">Destination</div><div className="mt-1 font-medium text-slate-900">{currentAlert.destination_ip || "-"}{currentAlert.destination_port ? `:${currentAlert.destination_port}` : ""}{currentAlert.network_protocol ? ` · ${currentAlert.network_protocol.toUpperCase()}` : ""}</div></div>
                    <div><div className="text-xs text-slate-400">Detected</div><div className="mt-1 text-slate-700">{formatDate(currentAlert.created_at)}</div></div>
                    <div><div className="text-xs text-slate-400">Event ID</div><div className="mt-1 font-mono text-xs text-slate-700">{currentAlert.event_id || "-"}</div></div>
                  </div>
                  <div className="mt-5 rounded-xl bg-slate-50 p-4">
                    <div className="text-xs font-medium text-slate-500">Raw message</div>
                    <div className="mt-2 whitespace-pre-wrap break-words font-mono text-xs leading-5 text-slate-700">{currentAlert.message || "-"}</div>
                  </div>
                </section>

                <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-slate-900">AI assessment</h3>
                      <p className="text-xs text-slate-500">Read-only response guidance</p>
                    </div>
                    {assessment && <Badge className={scoreClass(assessment.confidence)}>{typeof assessment.confidence === "number" ? assessment.confidence+"% confidence" : "Confidence unavailable"}</Badge>}
                  </div>
                  {assessment ? (
                    <div className="mt-4 space-y-4 text-sm">
                      <p className="leading-6 text-slate-700">{assessment.summary || "-"}</p>
                      <div className="rounded-xl border border-cyan-100 bg-cyan-50 p-4">
                        <div className="text-xs font-semibold uppercase tracking-wide text-cyan-700">Recommended response</div>
                        <div className="mt-1 font-semibold text-slate-900">{assessment.recommended_action || "Investigate"}</div>
                        {assessment.enforcement && <div className="mt-1 text-xs text-slate-500">AI guidance never bypasses tenant response policy.</div>}

                      </div>
                      <div>
                        <div className="text-xs font-medium text-slate-500">Why</div>
                        <ul className="mt-2 list-disc space-y-1 pl-5 text-slate-600">
                          {(assessment.reasoning || []).map((reason, index) => <li key={index}>{reason}</li>)}
                        </ul>
                      </div>
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Threat intelligence</div><div className="mt-1 text-sm font-semibold text-slate-900">{(intelProfile || assessment.threat_intelligence)?.reputation_label || "unknown"} · risk {(intelProfile || assessment.threat_intelligence)?.risk_score ?? "unknown"}/100</div></div>
                          {proposalError && <p role="alert" className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{proposalError}</p>}
                {proposalNotice && <p role="status" className="mt-3 rounded-lg border border-cyan-200 bg-cyan-50 p-3 text-sm text-cyan-800">{proposalNotice}</p>}
                {completedBlockResponse && <p className="mt-3 text-sm text-slate-600">This alert already has an executed block response. <a href={responseHref} className="font-semibold text-cyan-800 underline">View its saved response and history</a>.</p>}
                {canManage && currentAlert.source_ip && <button type="button" disabled={feedbackBusy} onClick={refreshThreatIntel} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50">Refresh intel</button>}
                        </div>
                        <div className="mt-3 grid gap-3 text-xs sm:grid-cols-2">
                          <div><span className="text-slate-400">Providers</span><div className="mt-1 text-slate-700">{asList((intelProfile || assessment.threat_intelligence)?.sources).join(", ") || "No fresh provider evidence"}</div></div>
                          <div><span className="text-slate-400">Evidence</span><div className="mt-1 text-slate-700">{(intelProfile || assessment.threat_intelligence)?.evidence_count ?? 0} signals</div></div>
                          <div><span className="text-slate-400">Network</span><div className="mt-1 text-slate-700">{(intelProfile || assessment.threat_intelligence)?.asn_org || (intelProfile || assessment.threat_intelligence)?.asn || "-"}</div></div>
                          <div><span className="text-slate-400">Country / domain</span><div className="mt-1 text-slate-700">{[(intelProfile || assessment.threat_intelligence)?.country_code, (intelProfile || assessment.threat_intelligence)?.domain].filter(Boolean).join(" · ") || "-"}</div></div>
                        </div>
                        {(assessment.confidence_breakdown || assessment.evidence?.length) && <div className="mt-4 border-t border-slate-200 pt-3"><div className="text-xs font-medium text-slate-500">Confidence breakdown</div><div className="mt-1 text-xs text-slate-600">Base {assessment.confidence_breakdown?.base ?? "-"}% · adjustments {assessment.confidence_breakdown?.adjustments > 0 ? "+" : ""}{assessment.confidence_breakdown?.adjustments ?? 0} · final {assessment.confidence_breakdown?.final ?? assessment.confidence}%</div></div>}
                      </div>

                      {assessment.correlation && <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Correlated incident</div><div className="mt-1 text-sm font-semibold text-slate-900">{assessment.correlation.title}</div></div>
                          <a href="/incidents" className="text-xs font-semibold text-slate-700 hover:text-slate-950">Open Incidents →</a>
                        </div>
                        <div className="mt-3 grid gap-3 text-xs sm:grid-cols-3"><div><span className="text-slate-400">Attack stage</span><div className="mt-1 text-slate-700">{assessment.correlation.attack_stage || "Observed Activity"}</div></div><div><span className="text-slate-400">Linked alerts</span><div className="mt-1 text-slate-700">{assessment.correlation.alert_count ?? 1}</div></div><div><span className="text-slate-400">Mapped techniques</span><div className="mt-1 text-slate-700">{assessment.correlation.technique_count ?? 0}</div></div></div>
                      </div>}

                      {isTenantAlert && <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <div className="flex flex-wrap items-center justify-between gap-3"><div><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Analyst feedback</div><div className="mt-1 text-xs text-slate-500">Labels improve future confidence and autonomy risk for this source.</div></div>{analystFeedback?.verdict && <Badge className="bg-slate-100 text-slate-700 ring-slate-200">{analystFeedback.verdict.replaceAll("_", " ")}</Badge>}</div>
                        {canManage ? <div className="mt-3 flex flex-wrap gap-2">{[["confirmed_threat", "Confirmed threat"], ["false_positive", "False positive"], ["benign", "Benign"], ["uncertain", "Uncertain"]].map(([value, label]) => <button key={value} type="button" disabled={feedbackBusy} onClick={() => submitAnalystFeedback(value)} className={`rounded-lg border px-3 py-2 text-xs font-semibold disabled:opacity-50 ${analystFeedback?.verdict === value ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>{label}</button>)}</div> : <div className="mt-3 text-xs text-slate-500">An organization admin can label this assessment outcome.</div>}
                      </div>}
                    </div>
                  ) : <div className="mt-4 text-sm text-slate-500">No assessment available.</div>}
                </section>
              </div>

              {isTenantAlert && detail && (
                <>


                  <div className="grid gap-5 xl:grid-cols-2">
                    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                      <h3 className="font-semibold text-slate-900">Analyst notes</h3>
                      {canManage && <div className="mt-4 flex gap-2"><textarea value={noteText} onChange={(event) => setNoteText(event.target.value)} rows="2" placeholder="Add investigation note..." className="min-w-0 flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" /><button type="button" onClick={addNote} disabled={saving || !noteText.trim()} className="self-end rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">Add note</button></div>}
                      <div className="mt-4 space-y-3">
                        {(detail.notes || []).length === 0 ? <div className="text-sm text-slate-500">No analyst notes yet.</div> : (detail.notes || []).map((note) => <div key={note.note_id} className="rounded-xl bg-slate-50 p-3"><div className="flex justify-between gap-3 text-xs text-slate-400"><span>{note.author?.name || "Analyst"}</span><span>{formatDate(note.created_at)}</span></div><p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{note.text}</p></div>)}
                      </div>
                    </section>

                    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                      <h3 className="font-semibold text-slate-900">Analyst activity</h3>
                      <div className="mt-4 space-y-3">
                        {(detail.activity || []).length === 0 ? <div className="text-sm text-slate-500">No workflow activity yet.</div> : (detail.activity || []).map((item) => <div key={item.activity_id} className="flex gap-3 text-sm"><div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-cyan-600" /><div><div className="font-medium text-slate-800">{String(item.type || "activity").replaceAll("_", " ")}</div><div className="text-xs text-slate-500">{item.actor?.name || "Analyst"} · {formatDate(item.created_at)}</div>{(item.from || item.to) && <div className="mt-1 text-xs text-slate-600">{item.from || "None"} → {item.to || "None"}</div>}</div></div>)}
                      </div>
                    </section>
                  </div>

                  <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex items-center justify-between"><div><h3 className="font-semibold text-slate-900">Event timeline</h3><p className="text-xs text-slate-500">Related tenant events within ±30 minutes</p></div><Badge className="bg-slate-100 text-slate-600 ring-slate-200">{(detail.related_events || []).length} events</Badge></div>
                    <div className="mt-4 overflow-x-auto"><table className="w-full text-sm"><thead className="bg-slate-50 text-slate-500"><tr><th className="p-3 text-left font-medium">Time</th><th className="p-3 text-left font-medium">Device</th><th className="p-3 text-left font-medium">Source IP</th><th className="p-3 text-left font-medium">Event</th><th className="p-3 text-left font-medium">Severity</th><th className="p-3 text-left font-medium">Message</th></tr></thead><tbody>{(detail.related_events || []).map((event) => <tr key={event.event_id} className="border-t border-slate-100"><td className="p-3 whitespace-nowrap text-slate-600">{formatDate(event.timestamp)}</td><td className="p-3 text-slate-600">{event.source_device || "-"}</td><td className="p-3 font-mono text-xs text-slate-700">{event.source_ip || "-"}</td><td className="p-3 text-slate-700">{event.event_type || "-"}</td><td className="p-3"><Badge className={severityClass(event.severity)}>{event.severity || "-"}</Badge></td><td className="max-w-md p-3 text-slate-600">{event.message || "-"}</td></tr>)}</tbody></table></div>
                  </section>

                  <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex items-center justify-between"><div><h3 className="font-semibold text-slate-900">Related alerts</h3><p className="text-xs text-slate-500">Same source IP or threat type within ±24 hours</p></div><Badge className="bg-slate-100 text-slate-600 ring-slate-200">{(detail.related_alerts || []).length}</Badge></div>
                    <div className="mt-4 grid gap-3 md:grid-cols-2">{(detail.related_alerts || []).length === 0 ? <div className="text-sm text-slate-500">No related alerts found.</div> : (detail.related_alerts || []).map((item) => <div key={item.alert_id} className="rounded-xl border border-slate-200 p-4"><div className="flex items-center justify-between gap-3"><div className="font-medium text-slate-900">{item.threat_type}</div><Badge className={statusClass(item.status)}>{item.status}</Badge></div><div className="mt-2 text-xs text-slate-500">{item.source_ip || "-"} · {formatDate(item.created_at)}</div></div>)}</div>
                  </section>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </dialog>
  );
}
